import { useState, useEffect, useRef } from "react";
import { trackGA } from "../helpers";

interface MarketoProps {
    baseUrl?: string;
    munchkinId: string;
    marketoFormId: string;
    callback?: (form: any) => void;
    prePopulatedFields?: { [key: string]: string };
    onSuccess?: () => void;
}

const appendMarketoScript = ({
    baseUrl,
    setScriptLoaded,
}: {
    baseUrl: string;
    setScriptLoaded: Function;
}) => {
    if ((window as any).MktoForms2) {
        setScriptLoaded(true);
    } else {
        const script = document.createElement("script");
        script.src = `${baseUrl}/js/forms2/js/forms2.min.js`;
        script.onload = () =>
            (window as any).MktoForms2 ? setScriptLoaded(true) : null;
        document.body.appendChild(script);
    }
};

export const useMarketo = ({
    baseUrl = "//page.oneocean.com",
    munchkinId,
    marketoFormId,
    prePopulatedFields,
    onSuccess,
}: MarketoProps) => {
    const [scriptLoaded, setScriptLoaded] = useState(false);

    const formRef = useRef<any>(null);
    const retriggerTimer = useRef<number | null>(null);
    const retryCount = useRef(0);
    const maxRetries = 3;
    const retryIntervalMs = 100;

    const attemptRetrigger = () => {
        const zifc = (window as any)._zi_fc;

        if (zifc?.fcRetrigger && !zifc.fcTriggered) {
            zifc.fcRetrigger();
            zifc.fcTriggered = true;
            console.info("fcRetrigger triggered under window._zi_fc");
        } else if (zifc?.fcRetrigger && zifc.fcTriggered) {
            console.info("fcRetrigger already triggered under window._zi_fc");
        } else {
            // only schedule another attempt if we haven't exceeded maxRetries
            if (retryCount.current < maxRetries) {
                retryCount.current += 1;
                retriggerTimer.current = window.setTimeout(
                    attemptRetrigger,
                    retryIntervalMs
                );
                console.debug(
                    `Retry #${retryCount.current} scheduled in ${retryIntervalMs}ms`
                );
            } else {
                console.warn(
                    `Max retrigger attempts (${maxRetries}) reached; window._zi_fc not found.`
                );
            }
        }
    };

    // 1. Load Marketoscript
    useEffect(() => {
        if (!scriptLoaded) {
            appendMarketoScript({ baseUrl, setScriptLoaded });
        }
    }, [baseUrl, scriptLoaded]);

    // 2. Initialize Marketo form when script is ready
    useEffect(() => {
        if (!scriptLoaded) return;
        if (!(window as any).MktoForms2) return;

        formRef.current = (window as any).MktoForms2.loadForm(
            baseUrl,
            munchkinId,
            marketoFormId,
            (form: any) => {
                // hide loader
                const loader = document.getElementById("marketoLoader");
                if (loader) loader.style.display = "none";

                // pre-populate fields
                if (prePopulatedFields) {
                    form.vals({ ...prePopulatedFields });
                }

                // custom cookie-based field
                const utmField = document.querySelector(
                    'input[name="Driver_ID_Most_Recent__c"]'
                ) as HTMLInputElement;
                if (utmField) {
                    const cookies = document.cookie.split("; ");
                    const urlParamsCookie = cookies
                        .map((c) => c.split("="))
                        .find(([key]) => key === "urlParams")?.[1];
                    if (urlParamsCookie) {
                        try {
                            const params = JSON.parse(decodeURIComponent(urlParamsCookie));
                            if (params.sfcid) {
                                utmField.value = params.sfcid;
                            }
                        } catch (error) {
                            console.error("Error parsing URL params cookie:", error);
                        }
                    }
                }

            // You cant submit over localhost so you can use onSubmit to test.
            // form.onSubmit(() => {
            //     formElement.hide();
            //     document.getElementById(`success-${marketoFormId}`)!.style.display = 'block';
            //     trackGA('marketo_submission', { title: marketoFormId });
            //     document.cookie = 'urlParams=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            //     if (onSuccess) onSuccess();
            //     return false;
            // });

                // onSuccess handler
                form.onSuccess(() => {
                    form.getFormElem().hide();
                    const successEl = document.getElementById(
                        `success-${marketoFormId}`
                    );
                    if (successEl) successEl.style.display = "block";

                    trackGA("marketo_submission", { title: marketoFormId });
                    document.cookie =
                        "urlParams=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

                    if (onSuccess) onSuccess();
                    return false; // prevent default post
                });
            }
        );

        // Re-trigger ZoomInfo when form has rendered
        (window as any).MktoForms2.whenRendered((form: any) => {
            // reset retry counter in case of re-render
            retryCount.current = 0;
            attemptRetrigger();
            // ensure form container is visible
            form.getFormElem()[0].removeAttribute("style");
        });

        return () => {
            // cleanup form instance
            if (formRef.current && (window as any).MktoForms2) {
                try {
                    const formEl = document.getElementById(
                        `mktoForm_${marketoFormId}`
                    );
                    if (formEl) {
                        (window as any).MktoForms2.getForm(marketoFormId)?.destroy();
                        console.info(`Marketo form ${marketoFormId} destroyed`);
                    }
                } catch (error) {
                    console.error("Error cleaning up Marketo form:", error);
                }
                formRef.current = null;
            }
            // clear any pending timer
            if (retriggerTimer.current) {
                clearTimeout(retriggerTimer.current);
            }
        };
    }, [
        scriptLoaded,
        baseUrl,
        munchkinId,
        marketoFormId,
        prePopulatedFields,
        onSuccess,
    ]);

    return {
        isLoaded: scriptLoaded,
        formInstance: formRef.current,
    };
};
